import { Close, Download, FitToScreen } from '@carbon/icons-react'

import React from 'react'
import SvgPreview from './PreviewSVG'
import pallete from '../../../global/newPallete'

interface MediaPreviewGridProps {
  attachedFiles: string[]
  onPreview?: (url: string) => void
  handleFileRemove?: (media: string) => void
  variant?: 'default' | 'close' | 'delete'
  download?: any
}

const MediaPreviewGrid: React.FC<MediaPreviewGridProps> = ({
  attachedFiles,
  onPreview,
  handleFileRemove,
  variant = 'default',
  download
}) => {
  return (
    <div className='flex flex-wrap rounded-lg w-full'>
      {attachedFiles?.map((media, idx) => {
        const fileName = media?.toLowerCase() || ''
        const fileType = media || ''

        // Extensions
        const isCode =
          /\.(json|ts|tsx|js|jsx|py|java|html|css|sh|yml|yaml)$/i.test(fileName)
        const isOffice = /\.(doc|docx|xls|xlsx|ppt|pptx)$/i.test(fileName)
        const isCSV = /\.csv$/i.test(fileName)
        const isSVG = fileName.endsWith('.svg')
        const isHeic =
          fileType.includes('heic') ||
          fileType.includes('heif') ||
          fileName.endsWith('.heic') ||
          fileName.endsWith('.heif')

        // Type-based
        const isImage = fileType.includes('image/') && !isHeic
        const isVideo = media.includes('video')
        const isPdf = fileType === 'application/pdf'
        const isDocx = fileType.includes('wordprocessingml.document')
        const isPptx = fileType.includes('presentationml.presentation')
        const isTextBased =
          fileType.startsWith('text/') || fileType.includes('json')

        // Final preview check
        const previewSupported =
          isImage ||
          isVideo ||
          isPdf ||
          isDocx ||
          isPptx ||
          isTextBased ||
          isCode ||
          isOffice ||
          isCSV

        return (
          <div key={idx} className='group'>
            {isVideo && (
              <div className='w-[192px] h-[128px] pr-[8px] flex justify-center relative'>
                <video
                  src={media}
                  className='object-cover rounded-md w-full h-full'
                  controls
                  muted
                  playsInline
                />
                {variant === 'close' && handleFileRemove && (
                  <button
                    className='absolute top-[4px] right-[4px] w-[24px] h-[24px] rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all duration-200 border-[2px]'
                    style={{
                      color: pallete.colors.lightWhite,
                      borderColor: pallete.colors.lightWhite
                    }}
                    onClick={() => handleFileRemove(media)}
                  >
                    <Close size={12} />
                  </button>
                )}
              </div>
            )}

            {isImage && (
              <div className='relative w-[200px] h-[128px] pr-[8px] pb-[8px]'>
                {isSVG ? (
                  <SvgPreview url={media} />
                ) : (
                  <img
                    src={media}
                    alt='media'
                    className='object-cover rounded-md w-full h-full'
                  />
                )}

                {variant !== 'delete' && (
                  <div className='absolute top-[4px] right-[12px] flex gap-[4px] z-10'>
                    {variant === 'default' && onPreview && (
                      <>
                        {!download && previewSupported && (
                          <button
                            className='text-white p-[4px] rounded-full hover:opacity-80 transition'
                            style={{
                              backgroundColor: pallete.colors.lightWhite
                            }}
                            onClick={() => onPreview(media)}
                          >
                            <FitToScreen size={12} />
                          </button>
                        )}

                        <a
                          href={media}
                          download
                          className=' text-white p-[4px] rounded-full hover:opacity-80 transition'
                          style={{
                            backgroundColor: pallete.colors.lightWhite
                          }}
                        >
                          <Download size={12} />
                        </a>
                      </>
                    )}

                    {variant === 'close' && handleFileRemove && (
                      <button
                        className=' text-white p-[4px] rounded-full hover:opacity-80 transition'
                        onClick={() => handleFileRemove(media)}
                        style={{
                          backgroundColor: pallete.colors.lightWhite
                        }}
                      >
                        <Close size={12} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default MediaPreviewGrid
